import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, Loader2, Zap, CheckCircle2,
  RefreshCw, LogOut,
  Smartphone, MessageSquare, Calendar,
  Link as LinkIcon, Trash2, Lock, Plus, X, Users
} from 'lucide-react';
import { auth } from '../config/firebase';
import {
  configuracaoService,
  whatsappService,
  googleService,
  profissionalService
} from '../services/api';
import CardInstancia from './CardInstancia';

const AutomacaoPage = ({ temAcesso, getLimite }) => {
  const isPremiumPlus = temAcesso('automacao_whatsapp');
  const isEmpresarial = temAcesso('google_multiplas_agendas');
  const [activeTab, setActiveTab] = useState(isPremiumPlus ? 'whatsapp' : 'google');
  const [loading, setLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [savedInstance, setSavedInstance] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  
  // Instância Principal
  // Controla se existe de fato uma instância principal salva no banco — não dá pra usar
  // `config.evol_instance_name` pra isso porque esse campo sempre tem um valor (o nome
  // automático pré-calculado pra quando o usuário for conectar pela primeira vez).
  const [instanciaPrincipalExiste, setInstanciaPrincipalExiste] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [statusConexao, setStatusConexao] = useState('DISCONNECTED');
  const [metodoConexao, setMetodoConexao] = useState('qr');
  const [telefonePareamento, setTelefonePareamento] = useState('');
  const [codigoPareamento, setCodigoPareamento] = useState(null);
  const [segundosRestantes, setSegundosRestantes] = useState(null);
  
  // Instâncias Adicionais (Plano Empresarial)
  const [instanciasAdicionais, setInstanciasAdicionais] = useState([]);
  const [isNovaInstanciaModalOpen, setIsNovaInstanciaModalOpen] = useState(false);
  const [novaInstanciaNome, setNovaInstanciaNome] = useState('');
  const [novaInstanciaMetodo, setNovaInstanciaMetodo] = useState('qr');
  const [novaInstanciaTelefone, setNovaInstanciaTelefone] = useState('');
  const [novaInstanciaQr, setNovaInstanciaQr] = useState(null);
  const [novaInstanciaCode, setNovaInstanciaCode] = useState(null);
  const [novaInstanciaLoading, setNovaInstanciaLoading] = useState(false);
  const [novaInstanciaSegundosRestantes, setNovaInstanciaSegundosRestantes] = useState(null);

  // Google Agenda & Profissionais
  const [agendaPrincipal, setAgendaPrincipal] = useState(null);
  const [agendasEquipe, setAgendasEquipe] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [isNovaAgendaModalOpen, setIsNovaAgendaModalOpen] = useState(false);
  const [selectedProfissionalId, setSelectedProfissionalId] = useState('');

  const [config, setConfig] = useState({
    evol_instance_name: '',
    mensagem_saudacao: '',
  });

  const fetchData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const autoName = `sincro_user_${user.uid.substring(0, 7).toLowerCase()}`;

        // obterWhatsapp traz a mensagem/apikey da instância principal; obterStatus é quem de
        // fato lista as instâncias adicionais (o campo instancias_adicionais nunca existiu na
        // resposta de obterWhatsapp, então "Conexões Secundárias" nunca aparecia na tela).
        const [resWhatsapp, resGoogle, resProfissionais, resStatus] = await Promise.all([
          configuracaoService.obterWhatsapp(user.uid).catch(() => ({ data: null })),
          googleService.verificarStatus(user.uid).catch(() => ({ data: { conectado: false, agendas_equipe: [] } })),
          profissionalService.listar(user.uid).catch(() => ({ data: [] })),
          whatsappService.obterStatus(user.uid).catch(() => ({ data: { instancias_adicionais: [] } }))
        ]);

        if (resProfissionais.data) {
          setProfissionais(resProfissionais.data);
        }

        if (resWhatsapp.data) {
          const w = resWhatsapp.data;
          const nomeInstancia = w.instancia_name || autoName;
          setConfig(prev => ({
            ...prev,
            evol_instance_name: nomeInstancia,
            mensagem_saudacao: w.mensagem_saudacao || '',
            evol_apikey: w.apikey_custom || '',
            bot_ativo: w.bot_ativo,
            telefone_conectado: w.telefone_conectado
          }));

          setSavedInstance(nomeInstancia);
          setSavedMessage(w.mensagem_saudacao || '');
          setStatusConexao(w.status_conexao);
          setInstanciaPrincipalExiste(true);
        } else {
          // Nenhuma instância principal salva (ou acabou de ser excluída agora). Sem esse reset
          // completo, o card da instância principal continuava na tela mostrando o status
          // antigo (ex: "Conectado") mesmo depois de excluída — e qualquer ação nele (pausar,
          // excluir de novo) falhava porque a instância já não existia mais no banco.
          setConfig(prev => ({
            ...prev,
            evol_instance_name: autoName,
            mensagem_saudacao: '',
            evol_apikey: '',
            bot_ativo: true,
            telefone_conectado: ''
          }));
          setSavedInstance('');
          setSavedMessage('');
          setStatusConexao('DISCONNECTED');
          setInstanciaPrincipalExiste(false);
          setQrCode(null);
          setCodigoPareamento(null);
        }

        setInstanciasAdicionais(resStatus.data?.instancias_adicionais || []);

        if (resGoogle.data) {
          if (resGoogle.data.conectado) {
            setAgendaPrincipal({ email: resGoogle.data.google_email });
          } else {
            setAgendaPrincipal(null);
          }
          setAgendasEquipe(resGoogle.data.agendas_equipe || []);
        }
      }
    } catch (error) { 
      console.error("Erro ao carregar dados:", error);
    } finally { 
      setInitialLoading(false); 
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Contagem regressiva do QR/código da instância principal (a Evolution/Baileys expira o
  // código depois de ~60s — sem isso, o usuário não sabia que o código na tela já não servia
  // mais e ficava tentando escanear um código morto).
  useEffect(() => {
    if (!qrCode && !codigoPareamento) {
      setSegundosRestantes(null);
      return;
    }
    setSegundosRestantes(60);
    const interval = setInterval(() => {
      setSegundosRestantes(prev => (prev === null || prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [qrCode, codigoPareamento]);

  // Mesma contagem, só que pro QR/código da instância adicional no modal
  useEffect(() => {
    if (!novaInstanciaQr && !novaInstanciaCode) {
      setNovaInstanciaSegundosRestantes(null);
      return;
    }
    setNovaInstanciaSegundosRestantes(60);
    const interval = setInterval(() => {
      setNovaInstanciaSegundosRestantes(prev => (prev === null || prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [novaInstanciaQr, novaInstanciaCode]);

  // Função para recarregar a tela silenciosamente ao excluir uma instância
  const handleInstanciaDeleted = () => {
    fetchData();
  };

  // ==========================================
  // FUNÇÕES DA INSTÂNCIA PRINCIPAL
  // ==========================================
  // /conectar e o antigo /reiniciar chamavam exatamente a mesma função no backend
  // (provisionarEConectar) — então tentar os dois em sequência nunca ajudava, só dobrava a
  // espera. O único caso que vale a pena tentar de novo automaticamente é quando a Evolution
  // diz que a instância já está ativa (RECONNECT_REQUIRED): geralmente é uma limpeza
  // assíncrona que ainda não terminou, e uma segunda tentativa poucos segundos depois resolve.
  const conectarComRetry = async (payload, jaTentouNovamente = false) => {
    const user = auth.currentUser;
    try {
      return await whatsappService.conectar(user.uid, payload);
    } catch (error) {
      if (error.response?.data?.action === 'RECONNECT_REQUIRED' && !jaTentouNovamente) {
        await new Promise(resolve => setTimeout(resolve, 2500));
        return conectarComRetry(payload, true);
      }
      throw error;
    }
  };

  const handleConectarWhatsApp = async () => {
    if (!config.mensagem_saudacao) return alert("Preencha a Mensagem primeiro.");
    if (config.evol_instance_name !== savedInstance || config.mensagem_saudacao !== savedMessage) {
      return alert("Salve as alterações antes de conectar.");
    }

    setQrLoading(true);
    try {
      const response = await conectarComRetry({ instanceName: config.evol_instance_name });
      if (response.data.qrcode) {
        setQrCode(response.data.qrcode);
        setCodigoPareamento(null);
        setStatusConexao('CONNECTING');
      }
    } catch (error) {
      alert(error.response?.data?.error || "Erro ao gerar QR Code. Tente novamente em instantes.");
    } finally {
      setQrLoading(false);
    }
  };

  const handleGerarCodigoCelular = async () => {
    if (!telefonePareamento || telefonePareamento.length < 10) return alert("Número inválido.");
    setQrLoading(true);
    try {
      const response = await conectarComRetry({
        instanceName: config.evol_instance_name,
        number: "55" + telefonePareamento
      });
      if (response.data.code) {
        setCodigoPareamento(response.data.code);
        setQrCode(null);
        setStatusConexao('CONNECTING');
      }
    } catch (error) {
      alert(error.response?.data?.error || "Erro ao gerar código. Tente novamente em instantes.");
    } finally {
      setQrLoading(false);
    }
  };

  const tentarNovamente = () => {
    setQrCode(null);
    setCodigoPareamento(null);
    if (metodoConexao === 'qr') handleConectarWhatsApp();
    else handleGerarCodigoCelular();
  };

  const handleDesconectarWA = async () => {
    if (!window.confirm("O robô vai parar de responder seus clientes até você reconectar. Deseja desconectar mesmo assim?")) return;
    setLoading(true);
    try {
      const user = auth.currentUser;
      await whatsappService.desconectar(user.uid); 
      setQrCode(null);
      setCodigoPareamento(null);
      setStatusConexao('DISCONNECTED');
      fetchData();
    } catch (error) { alert("Erro ao desconectar."); } finally { setLoading(false); }
  };

  // ==========================================
  // FUNÇÕES DE INSTÂNCIAS ADICIONAIS (EMPRESARIAL)
  // ==========================================
  const abrirModalNovaInstancia = () => {
    const user = auth.currentUser;
    const baseName = `sincro_user_${user.uid.substring(0, 7).toLowerCase()}`;
    const sulfixo = Math.floor(1000 + Math.random() * 9000);
    
    setNovaInstanciaNome(`${baseName}_add_${sulfixo}`);
    setNovaInstanciaQr(null);
    setNovaInstanciaCode(null);
    setNovaInstanciaTelefone('');
    setIsNovaInstanciaModalOpen(true);
  };

  const handleGerarNovaInstancia = async () => {
    setNovaInstanciaLoading(true);
    try {
      const user = auth.currentUser;
      const payload = { instanceName: novaInstanciaNome };

      if (novaInstanciaMetodo === 'codigo') {
        if (!novaInstanciaTelefone || novaInstanciaTelefone.length < 10) return alert("Número inválido.");
        payload.number = "55" + novaInstanciaTelefone;
      }

      const response = await whatsappService.conectar(user.uid, payload);
      
      if (response.data.qrcode) {
        setNovaInstanciaQr(response.data.qrcode);
      }
      if (response.data.code) {
        setNovaInstanciaCode(response.data.code);
      }
    } catch (error) {
      alert(error.response?.data?.error || "Erro ao criar nova conexão de WhatsApp. Tente novamente.");
    } finally {
      setNovaInstanciaLoading(false);
    }
  };

  const fecharModalNovaInstancia = () => {
    setIsNovaInstanciaModalOpen(false);
    fetchData(); 
  };

  // ==========================================
  // GOOGLE E CONFIGURAÇÕES GERAIS
  // ==========================================
  const handleConnectGoogle = async (profissionalId = null) => {
    try {
      const user = auth.currentUser;
      const response = await googleService.gerarUrlAuth(user.uid, profissionalId);
      window.location.href = response.data.url;
    } catch (err) { alert("Erro ao gerar link de conexão."); }
  };

  const handleDisconnectGoogle = async (profissionalId = null) => {
    if (window.confirm("Deseja remover este vínculo de agenda?")) {
      try {
        const user = auth.currentUser;
        await googleService.desconectar(user.uid, profissionalId);
        fetchData();
      } catch (err) { alert("Erro ao desconectar."); }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveSuccess(false);
    try {
      const user = auth.currentUser;
      if (activeTab === 'whatsapp') {
        const dadosWhats = {
          evol_instance_name: config.evol_instance_name,
          evol_apikey: config.evol_apikey,
          mensagem_saudacao: config.mensagem_saudacao
        };
        await configuracaoService.salvarWhatsapp(user.uid, dadosWhats);
        setSavedInstance(config.evol_instance_name);
        setSavedMessage(config.mensagem_saudacao);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) { alert("Erro ao salvar."); } finally { setLoading(false); }
  };

  if (initialLoading) return (
    <div className="flex flex-col items-center justify-center py-24 text-center text-slate-400 font-black uppercase text-xs tracking-[0.2em]">
      <Loader2 className="animate-spin text-[#1883FF] mb-4" size={42} />
      <p>Carregando suas conexões...</p>
    </div>
  );

  const totalInstancias = (instanciaPrincipalExiste ? 1 : 0) + instanciasAdicionais.length;
  const limiteInstancias = getLimite('whatsapp_instancias');

  // Passo do fluxo de conexão pra situar o usuário: 1) escrever e salvar a mensagem, 2) gerar
  // o QR/código e escanear, 3) já conectado.
  const passoConexao = statusConexao === 'CONNECTED'
    ? 3
    : (qrCode || codigoPareamento || statusConexao === 'CONNECTING') ? 2 : 1;

  // Filtra profissionais que ainda não têm agenda vinculada para exibir no modal
  const profissionaisDisponiveis = profissionais.filter(
    p => !agendasEquipe.some(agenda => agenda.profissional_id === p.id)
  );

  return (
    <div className="w-full max-w-[950px] mx-auto p-4 box-border overflow-x-hidden font-sans text-left">
      
      {/* Abas Alternadoras de Seções */}
      <div className="bg-[#F8F7FF] p-1.5 rounded-[20px] flex flex-wrap gap-1.5 mb-8 w-full box-border border border-purple-50">
        <button
          className={`flex-1 min-w-[120px] justify-center py-3 px-4 border-none rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${!isPremiumPlus ? 'cursor-not-allowed opacity-50 bg-transparent text-slate-400' : activeTab === 'whatsapp' ? 'bg-white text-[#6D28D9] shadow-sm cursor-pointer' : 'bg-transparent text-slate-400 cursor-pointer'}`}
          onClick={() => isPremiumPlus && setActiveTab('whatsapp')}
          disabled={!isPremiumPlus}
          title={!isPremiumPlus ? 'Disponível a partir do plano Premium' : undefined}
        >
          {isPremiumPlus ? <Smartphone size={16} /> : <Lock size={16} />} Automação WhatsApp
        </button>
        <button className={`flex-1 min-w-[120px] justify-center py-3 px-4 border-none rounded-2xl cursor-pointer text-[10px] font-black uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${activeTab === 'google' ? 'bg-white text-[#6D28D9] shadow-sm' : 'bg-transparent text-slate-400'}`} onClick={() => setActiveTab('google')}>
          <Calendar size={16} /> Google Agenda
        </button>
      </div>

      {/* Card de Configuração Central */}
      <div className="bg-white rounded-[32px] p-6 sm:p-10 border border-slate-100 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.03)] w-full box-border">
        
        {/* SEÇÃO: ABA WHATSAPP (recurso pago) */}
        {activeTab === 'whatsapp' && isPremiumPlus && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-8 sm:gap-10 w-full box-border animate-fade-in-up">
            
            {/* Bloco Esquerdo: Mensagem */}
            <div className="min-w-0 w-full text-left">
              <div className="flex items-center gap-3 mb-6 border-b border-purple-50 pb-4">
                <MessageSquare size={18} className="text-[#1883FF]" />
                <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Mensagens e Identificação</h3>
              </div>
              
              <div className="flex flex-col gap-2 w-full box-border">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider ml-1">Mensagem de Boas-vindas</label>
                <textarea rows="5" className="w-full bg-[#F8F7FF] border-2 border-transparent p-3.5 px-4 rounded-2xl text-sm font-bold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9]/30 focus:ring-4 focus:ring-[#6D28D9]/10 min-h-[110px] font-sans" value={config.mensagem_saudacao} placeholder="Olá! Como posso ajudar?" onChange={(e) => setConfig({...config, mensagem_saudacao: e.target.value})} />
                <p className="text-[10px] color-[#94a3b8] text-slate-400 font-semibold mt-1.5 ml-1">
                  Esta mensagem será enviada automaticamente pelo robô no primeiro contato do paciente.
                </p>
              </div>
            </div>

            {/* Bloco Direito: Pareamentos e Instância Mestre */}
            <div className="flex flex-col gap-5 w-full box-border">

              {/* Indicador de progresso: situa o usuário em qual passo da conexão ele está */}
              <div className="flex items-center gap-1.5 px-1">
                {['Mensagem', 'Conectar', 'Conectado'].map((label, i) => {
                  const passo = i + 1;
                  const ativo = passo <= passoConexao;
                  return (
                    <React.Fragment key={label}>
                      <div className={`flex items-center gap-1 text-[8px] font-black uppercase tracking-wide whitespace-nowrap ${ativo ? 'text-[#6D28D9]' : 'text-slate-300'}`}>
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] ${ativo ? 'bg-[#6D28D9] text-white' : 'bg-slate-100 text-slate-400'}`}>{passo}</span>
                        {label}
                      </div>
                      {passo < 3 && <div className={`flex-1 h-0.5 rounded-full ${passoConexao > passo ? 'bg-[#6D28D9]' : 'bg-slate-100'}`} />}
                    </React.Fragment>
                  );
                })}
              </div>

              <div className={`bg-[#F8F7FF] border-2 border-dashed border-purple-100 rounded-[28px] p-6 flex flex-col items-center text-center w-full box-border ${statusConexao === 'CONNECTED' ? 'bg-green-50 border-solid border-green-200' : statusConexao === 'CONNECTING' ? 'bg-amber-50 border-solid border-amber-200' : ''}`}>
                <Zap size={30} className={statusConexao === 'CONNECTED' ? 'text-green-500' : statusConexao === 'CONNECTING' ? 'text-amber-500' : 'text-slate-300'} />
                <h4 className="text-[11px] font-black uppercase tracking-wider mt-4 mb-1">WhatsApp (Principal)</h4>
                <p className={`text-[9px] font-black uppercase tracking-wide mb-4 ${statusConexao === 'CONNECTED' ? 'text-green-600' : statusConexao === 'CONNECTING' ? 'text-amber-600' : 'text-slate-500'}`}>
                  {statusConexao === 'CONNECTED' ? '● Conectado' : statusConexao === 'CONNECTING' ? '● Aguardando leitura...' : '○ Desconectado'}
                </p>

                {statusConexao !== 'CONNECTED' && (
                  <div className="w-full flex flex-col gap-3">
                    <div className="flex gap-2 bg-slate-200 p-1 rounded-xl w-full">
                      <button onClick={() => setMetodoConexao('qr')} className={`flex-1 border-none py-2 text-[10px] font-black rounded-lg cursor-pointer transition-all ${metodoConexao === 'qr' ? 'bg-white text-[#1883FF]' : 'bg-transparent text-slate-500'}`}>Usar QR Code</button>
                      <button onClick={() => setMetodoConexao('codigo')} className={`flex-1 border-none py-2 text-[10px] font-black rounded-lg cursor-pointer transition-all ${metodoConexao === 'codigo' ? 'bg-white text-[#1883FF]' : 'bg-transparent text-slate-500'}`}>Código (Celular)</button>
                    </div>

                    {/* QR Code */}
                    {metodoConexao === 'qr' && (
                      <div className="w-full flex flex-col gap-3">
                        {qrCode && (
                          <div className={`bg-white p-2.5 rounded-2xl w-full border shadow-sm ${segundosRestantes === 0 ? 'border-red-200 opacity-40' : 'border-slate-100'}`}>
                            <img src={`data:image/png;base64,${qrCode}`} alt="QR Code de Conexão" className="w-full h-auto block rounded-lg" />
                          </div>
                        )}
                        {qrCode && segundosRestantes === 0 ? (
                          <p className="text-[10px] text-red-500 font-bold m-0">Esse código expirou. Gere um novo abaixo.</p>
                        ) : qrCode && segundosRestantes !== null ? (
                          <p className="text-[10px] text-slate-500 font-medium m-0">Abra o WhatsApp no celular e escaneie o código acima. Expira em {segundosRestantes}s.</p>
                        ) : (
                          <p className="text-[10px] text-slate-500 font-medium m-0">Abra o WhatsApp no celular e escaneie o código acima.</p>
                        )}
                        <button onClick={qrCode ? tentarNovamente : handleConectarWhatsApp} className={`w-full h-11 text-white border-none rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-colors ${qrCode && segundosRestantes === 0 ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-green-500 hover:bg-green-600'}`}>
                          {qrLoading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                          {qrCode ? "Gerar Novo QR Code" : "Gerar QR Code"}
                        </button>
                      </div>
                    )}

                    {/* Código de Pareamento */}
                    {metodoConexao === 'codigo' && (
                      <div className="w-full flex flex-col gap-3 text-left">
                        {codigoPareamento ? (
                          <div className="bg-[#F5F3FF] p-5 rounded-2xl border-2 border-dashed border-[#6D28D9]/30 w-full text-center">
                            <p className="text-[9px] font-black text-[#1883FF] uppercase tracking-wider mb-2 m-0">Seu Código de Conexão:</p>
                            <h2 className={`font-black m-0 ${segundosRestantes === 0 ? 'text-slate-300' : 'text-slate-800'} ${codigoPareamento.length > 10 ? 'text-base tracking-normal' : 'text-2xl tracking-widest'}`}>{codigoPareamento}</h2>
                            {segundosRestantes === 0 ? (
                              <p className="text-[10px] text-red-500 font-bold mt-2 mb-0">Esse código expirou.</p>
                            ) : segundosRestantes !== null ? (
                              <p className="text-[10px] text-slate-400 font-semibold mt-2 mb-0">Expira em {segundosRestantes}s</p>
                            ) : null}

                            <div className="text-[10px] text-slate-500 font-medium mt-4 leading-relaxed text-left">
                              1. Abra o WhatsApp e vá em <b>Aparelhos Conectados</b>.<br />
                              2. Clique em <b>Conectar Aparelho</b>.<br />
                              3. Escolha <b>"Conectar com número de telefone"</b>.<br />
                              4. Digite o código acima.

                              <div onClick={tentarNovamente} className={`cursor-pointer font-black mt-3 flex items-center justify-center gap-1 hover:underline ${segundosRestantes === 0 ? 'text-red-500' : 'text-[#1883FF]'}`}>
                                <RefreshCw size={10} /> GERAR OUTRO CÓDIGO
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full bg-[#F8F7FF] p-2.5 rounded-2xl flex flex-col gap-2 border border-purple-50">
                            <input type="text" placeholder="DDD + Seu Número (Ex: 85999999999)" value={telefonePareamento} onChange={(e) => setTelefonePareamento(e.target.value.replace(/\D/g, ''))} className="w-full bg-white border border-purple-100 py-3 rounded-xl text-sm font-bold text-center outline-none focus:border-[#6D28D9]" />
                            <p className="text-[10px] text-slate-500 font-medium m-0 text-center px-1">Este deve ser o número do celular onde o WhatsApp está instalado.</p>
                            <button onClick={handleGerarCodigoCelular} className="w-full h-11 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer hover:opacity-90 transition-opacity shadow-sm shadow-[#6D28D9]/20" disabled={qrLoading}>
                              {qrLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} 
                              {qrLoading ? "Gerando..." : "Gerar Código"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {statusConexao === 'CONNECTED' && (
                  <button onClick={handleDesconectarWA} className="w-full h-11 bg-red-500 text-white border-none rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer hover:bg-red-600 transition-colors mt-2">
                    <LogOut size={14} /> Desconectar Aparelho
                  </button>
                )}
              </div>

              {/* Instância ativa principal */}
              {instanciaPrincipalExiste && (
                <CardInstancia
                  instancia={{
                    name: config.evol_instance_name,
                    number: config.telefone_conectado || '',
                    bot_active: config.bot_ativo !== undefined ? config.bot_ativo : true,
                    status_conexao: statusConexao
                  }}
                  onDelete={handleInstanciaDeleted}
                />
              )}

              {/* RENDERIZAR INSTÂNCIAS ADICIONAIS JÁ CONECTADAS */}
              {instanciasAdicionais.length > 0 && (
                <div className="flex flex-col gap-3 w-full mt-2">
                  <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-wider m-0 text-center">Conexões Secundárias</h4>
                  {instanciasAdicionais.map((inst) => (
                    <CardInstancia
                      key={inst.instancia_name}
                      instancia={{
                        name: inst.instancia_name,
                        number: inst.telefone_conectado || 'Add-on',
                        bot_active: inst.bot_ativo !== undefined ? inst.bot_ativo : true,
                        status_conexao: inst.status_conexao
                      }}
                      onDelete={handleInstanciaDeleted}
                    />
                  ))}
                </div>
              )}

              {/* PAYWALL / ADICIONAR INSTÂNCIAS (LIMITE POR PLANO) */}
              {limiteInstancias === 0 ? (
                <div className="relative mt-2 rounded-[28px] border border-purple-50 bg-[#F8F7FF] p-6 flex flex-col items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-4 text-center">
                    <span className="flex items-center gap-1 text-[10px] font-black uppercase bg-amber-100 text-amber-700 px-3 py-1 rounded-md mb-2 shadow-sm">
                      <Lock size={12} /> Plano Premium
                    </span>
                    <p className="text-sm font-black text-slate-800 m-0 mt-1">Múltiplos Números</p>
                    <p className="text-[10px] text-slate-500 font-semibold mt-1">Conecte WhatsApps adicionais para recepção, vendas ou membros da equipe.</p>
                  </div>
                  <div className="opacity-30 flex items-center justify-between w-full max-w-[200px] border-2 border-dashed border-purple-100 p-4 rounded-xl">
                    <div className="w-10 h-10 bg-slate-300 rounded-full"></div>
                    <div className="flex-1 ml-3 h-4 bg-slate-300 rounded-md"></div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2 mt-2">
                  <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider m-0 text-center">
                    {totalInstancias} de {limiteInstancias} conexões
                  </p>
                  {totalInstancias < limiteInstancias && (
                    <button
                      onClick={abrirModalNovaInstancia}
                      className="w-full bg-transparent border-2 border-dashed border-[#6D28D9]/30 text-[#6D28D9] font-black text-[10px] uppercase py-3 rounded-xl hover:bg-[#F8F7FF] transition-colors flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Plus size={14} /> Adicionar Nova Conexão
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SEÇÃO: ABA GOOGLE */}
        {activeTab === 'google' && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-8 sm:gap-10 w-full box-border animate-fade-in-up text-left">
            <div>
              <div className="flex items-center gap-3 mb-4 border-b border-purple-50 pb-4">
                <Calendar size={18} className="text-[#1883FF]" />
                <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Sincronização Cloud</h3>
              </div>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 m-0">Conecte sua conta para sincronizar compromissos automaticamente.</p>
              
              <section className="mb-6">
                <h3 className="text-[10px] font-black uppercase text-slate-400 mb-3 tracking-wider ml-1">Agenda Principal</h3>
                <div className="bg-white border border-purple-50 p-5 rounded-2xl flex items-center justify-between shadow-sm">
                  <div>
                    <p className="text-[10px] font-black text-[#1883FF] uppercase mb-1">Email Vinculado</p>
                    <p className="text-sm font-bold text-slate-700 m-0">{agendaPrincipal?.email || 'Nenhuma conta principal'}</p>
                  </div>
                  {agendaPrincipal ? (
                    <button onClick={() => handleDisconnectGoogle(null)} className="text-red-500 hover:text-red-600 p-2 cursor-pointer transition-colors bg-red-50 rounded-lg">
                      <Trash2 size={18} />
                    </button>
                  ) : (
                    <button onClick={() => handleConnectGoogle(null)} className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase shadow-md cursor-pointer hover:opacity-90 flex gap-2 items-center">
                      <LinkIcon size={14} /> Conectar
                    </button>
                  )}
                </div>
              </section>

              {isEmpresarial && (
                <section>
                  <h3 className="text-[10px] font-black uppercase text-slate-400 mb-3 tracking-wider ml-1 mt-8">Agendas da Equipe</h3>
                  <div className="grid gap-3">
                    {agendasEquipe.map((agenda, i) => {
                      const prof = profissionais.find(p => p.id === agenda.profissional_id);
                      return (
                        <div key={i} className="bg-[#F8F7FF] p-4 rounded-xl flex items-center justify-between border border-purple-100">
                          <div>
                            <p className="text-sm font-bold text-slate-700 m-0 mb-1">{agenda.google_email}</p>
                            <p className="text-[9px] text-[#6D28D9] uppercase font-black m-0 bg-white inline-block px-2 py-0.5 rounded-md border border-purple-50">
                              {prof ? prof.nome : `ID Profissional: ${agenda.profissional_id}`}
                            </p>
                          </div>
                          <button onClick={() => handleDisconnectGoogle(agenda.profissional_id)} className="text-red-400 hover:text-red-600 bg-white p-2 rounded-lg cursor-pointer transition-colors shadow-sm">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                    
                    <button onClick={() => setIsNovaAgendaModalOpen(true)} className="w-full mt-2 bg-transparent border-2 border-dashed border-[#6D28D9]/30 text-[#6D28D9] font-black text-[10px] uppercase py-3.5 rounded-xl hover:bg-[#F8F7FF] transition-colors flex items-center justify-center gap-2 cursor-pointer">
                      <Plus size={14} /> Vincular Agenda de Profissional
                    </button>
                  </div>
                </section>
              )}
            </div>
            
            <div className="flex flex-col gap-5 w-full">
              {!isEmpresarial && (
                <div className="relative mt-2 rounded-[28px] border border-purple-50 bg-[#F8F7FF] p-6 flex flex-col items-center justify-center overflow-hidden h-full min-h-[250px]">
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-4 text-center">
                    <span className="flex items-center gap-1 text-[10px] font-black uppercase bg-amber-100 text-amber-700 px-3 py-1 rounded-md mb-2 shadow-sm">
                      <Lock size={12} /> Plano Empresarial
                    </span>
                    <p className="text-sm font-black text-slate-800 m-0 mt-1">Múltiplas Agendas (Equipe)</p>
                    <p className="text-[10px] text-slate-500 font-semibold mt-1">Conecte o calendário de cada profissional para o robô rotear os horários automaticamente.</p>
                  </div>
                  <div className="opacity-30 flex items-center justify-between w-full max-w-[200px] border-2 border-dashed border-purple-100 p-4 rounded-xl">
                    <div className="w-10 h-10 bg-slate-300 rounded-full"></div>
                    <div className="flex-1 ml-3 h-4 bg-slate-300 rounded-md"></div>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* Botão de Salvamento */}
        <button
          className={`w-full h-14 border-none rounded-2xl text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2.5 transition-all duration-400 cursor-pointer shadow-md mt-10 active:scale-98 ${saveSuccess ? 'bg-[#059669] shadow-emerald-600/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:opacity-90 shadow-lg shadow-[#6D28D9]/20 hover:-translate-y-0.5'}`}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : saveSuccess ? <CheckCircle2 size={18} /> : <Save size={18} />}
          {loading ? "Processando..." : saveSuccess ? "Configurações Aplicadas!" : "Salvar Alterações"}
        </button>
      </div>

      {/* MODAL DE CRIAÇÃO DE NOVA INSTÂNCIA (EMPRESARIAL) */}
      {isNovaInstanciaModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[400px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh] text-center">
            
            <div className="flex justify-between items-center mb-6 shrink-0 border-b border-slate-50 pb-4">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-base m-0 flex items-center gap-2">
                <Smartphone size={18} className="text-[#1883FF]" />
                Nova Conexão
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={fecharModalNovaInstancia}>
                <X size={18}/>
              </button>
            </div>

            <p className="text-[10px] text-slate-400 font-bold mb-4 uppercase">Conexão Adicional #{instanciasAdicionais.length + 1}</p>

            <div className="w-full flex flex-col gap-3">
              <div className="flex gap-2 bg-slate-200 p-1 rounded-xl w-full">
                <button onClick={() => setNovaInstanciaMetodo('qr')} className={`flex-1 border-none py-2 text-[10px] font-black rounded-lg cursor-pointer transition-all ${novaInstanciaMetodo === 'qr' ? 'bg-white text-[#1883FF]' : 'bg-transparent text-slate-500'}`}>Usar QR Code</button>
                <button onClick={() => setNovaInstanciaMetodo('codigo')} className={`flex-1 border-none py-2 text-[10px] font-black rounded-lg cursor-pointer transition-all ${novaInstanciaMetodo === 'codigo' ? 'bg-white text-[#1883FF]' : 'bg-transparent text-slate-500'}`}>Código (Celular)</button>
              </div>

              {/* Fluxo QR Code */}
              {novaInstanciaMetodo === 'qr' && (
                <div className="w-full flex flex-col gap-3">
                  {novaInstanciaQr && (
                    <div className={`bg-white p-2.5 rounded-2xl w-full border shadow-sm ${novaInstanciaSegundosRestantes === 0 ? 'border-red-200 opacity-40' : 'border-slate-100'}`}>
                      <img src={`data:image/png;base64,${novaInstanciaQr}`} alt="QR Code" className="w-full h-auto block rounded-lg" />
                    </div>
                  )}
                  {novaInstanciaQr && novaInstanciaSegundosRestantes === 0 ? (
                    <p className="text-[10px] text-red-500 font-bold m-0">Esse código expirou. Gere um novo abaixo.</p>
                  ) : novaInstanciaQr && novaInstanciaSegundosRestantes !== null ? (
                    <p className="text-[10px] text-slate-500 font-medium m-0">Escaneie o código para conectar. Expira em {novaInstanciaSegundosRestantes}s.</p>
                  ) : null}
                  <button onClick={handleGerarNovaInstancia} className={`w-full h-11 text-white border-none rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-colors ${novaInstanciaQr && novaInstanciaSegundosRestantes === 0 ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-green-500 hover:bg-green-600'}`}>
                    {novaInstanciaLoading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                    {novaInstanciaQr ? "Gerar Novo QR Code" : "Gerar QR Code"}
                  </button>
                </div>
              )}

              {/* Fluxo Código de Pareamento */}
              {novaInstanciaMetodo === 'codigo' && (
                <div className="w-full flex flex-col gap-3 text-left">
                  {novaInstanciaCode ? (
                    <div className="bg-[#F5F3FF] p-5 rounded-2xl border-2 border-dashed border-[#6D28D9]/30 w-full text-center">
                      <p className="text-[9px] font-black text-[#1883FF] uppercase tracking-wider mb-2 m-0">Código de Conexão:</p>
                      <h2 className={`font-black m-0 text-2xl tracking-widest ${novaInstanciaSegundosRestantes === 0 ? 'text-slate-300' : 'text-slate-800'}`}>{novaInstanciaCode}</h2>
                      {novaInstanciaSegundosRestantes === 0 ? (
                        <p className="text-[10px] text-red-500 font-bold mt-2 mb-0">Esse código expirou.</p>
                      ) : novaInstanciaSegundosRestantes !== null ? (
                        <p className="text-[10px] text-slate-400 font-semibold mt-2 mb-0">Expira em {novaInstanciaSegundosRestantes}s</p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="w-full bg-[#F8F7FF] p-2.5 rounded-2xl flex flex-col gap-2 border border-purple-50">
                      <input type="text" placeholder="DDD + Seu Número (Ex: 85999999999)" value={novaInstanciaTelefone} onChange={(e) => setNovaInstanciaTelefone(e.target.value.replace(/\D/g, ''))} className="w-full bg-white border border-purple-100 py-3 rounded-xl text-sm font-bold text-center outline-none focus:border-[#6D28D9]" />
                      <button onClick={handleGerarNovaInstancia} className="w-full h-11 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer hover:opacity-90 transition-opacity" disabled={novaInstanciaLoading}>
                        {novaInstanciaLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />} 
                        {novaInstanciaLoading ? "Gerando..." : "Gerar Código"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <p className="text-[9px] text-slate-400 font-semibold mt-4 mb-0">Feche esta janela após concluir a conexão no aparelho celular.</p>

          </div>
        </div>
      )}

      {/* MODAL PARA SELECIONAR PROFISSIONAL (AGENDA EQUIPE) */}
      {isNovaAgendaModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[400px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh] text-left">
            
            <div className="flex justify-between items-center mb-6 shrink-0 border-b border-slate-50 pb-4">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-base m-0 flex items-center gap-2">
                <Users size={18} className="text-[#6D28D9]" />
                Vincular Profissional
              </h3>
              <button 
                className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" 
                onClick={() => {
                  setIsNovaAgendaModalOpen(false);
                  setSelectedProfissionalId('');
                }}
              >
                <X size={18}/>
              </button>
            </div>

            <p className="text-[11px] text-slate-500 font-medium mb-5 leading-relaxed">
              Selecione o membro da equipe que você deseja vincular à conta do Google Agenda.
            </p>

            <div className="w-full flex flex-col gap-4">
              {profissionaisDisponiveis.length > 0 ? (
                <>
                  <select 
                    value={selectedProfissionalId}
                    onChange={(e) => setSelectedProfissionalId(e.target.value)}
                    className="w-full bg-[#F8F7FF] border border-purple-100 p-3.5 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[#6D28D9]"
                  >
                    <option value="" disabled>Selecione um profissional...</option>
                    {profissionaisDisponiveis.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>

                  <button 
                    onClick={() => {
                      if (selectedProfissionalId) {
                        handleConnectGoogle(selectedProfissionalId);
                        setIsNovaAgendaModalOpen(false);
                      }
                    }} 
                    disabled={!selectedProfissionalId}
                    className="w-full h-12 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  >
                    <LinkIcon size={16} /> Autenticar no Google
                  </button>
                </>
              ) : (
                <div className="text-center py-6 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 m-0">Todos os profissionais já possuem agenda vinculada ou não há profissionais cadastrados.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AutomacaoPage;